/**
 * Realistic sample values for email-template placeholders.
 *
 * Used by both the Template Editor preview pane AND the Send Test
 * dialog so the rendered preview reads like a real corporate email
 * ("Hi Alex Morgan,") instead of `Sample name` literal text every
 * admin had to manually overwrite before sending a test.
 *
 * Add a key here whenever a new template variable is introduced —
 * unknown variables fall back to `Sample <var>` so nothing breaks if
 * the catalogue adds a new one before this file catches up.
 */

export const EMAIL_SAMPLE_VALUES = {
  // ── People / recipients ──────────────────────────────────────────
  name: 'Alex Morgan',
  recipient_name: 'Alex Morgan',
  admin_name: 'Sarah Lin',
  inviter_name: 'Sarah Lin',
  invitee_name: 'Alex Morgan',
  assigner_name: 'Sarah Lin',
  unassigner_name: 'Sarah Lin',
  actor_name: 'Sarah Lin',
  commenter_name: 'Sarah Lin',
  mentioner_name: 'Sarah Lin',
  customer_name: 'Alex Morgan',
  inactive_user_name: 'Jordan Park',

  email: 'alex.morgan@acme.com',
  invitee_email: 'alex.morgan@acme.com',
  inactive_user_email: 'jordan.park@acme.com',

  // ── Containers ───────────────────────────────────────────────────
  company_name: 'Acme Manufacturing',
  // Mirrors the auto-created starter Workspace convention: "{Company} - Workspace".
  workspace_name: 'Acme Manufacturing - Workspace',
  space_name: 'Q4 Roadmap',
  department_name: 'Operations',
  container_kind: 'Space',
  container_name: 'Q4 Roadmap',

  // ── Tasks ────────────────────────────────────────────────────────
  task_name: 'Finalise Q4 budget proposal',
  comment_body: 'Numbers look strong — adding a few inline notes on the marketing line.',
  reply_body: 'Thanks for reaching out. The fix has been rolled out and the issue should be resolved.',

  // ── Status / role / access transitions ──────────────────────────
  old_status: 'In Progress',
  new_status: 'Done',
  old_role: 'Employee',
  new_role: 'Team Leader',
  old_access: 'View',
  new_access: 'Edit',

  // ── Time / dates ─────────────────────────────────────────────────
  report_date: '04 Jun 2026',
  week_start: '29 May 2026',
  week_end: '04 Jun 2026',
  month_label: 'May 2026',
  due_date: '10 Jun 2026',
  hours_remaining: '24',
  hours_overdue: '6',
  hours_since_missed: '24',
  expiry_date: '31 Dec 2026',
  expiry_minutes: '30',
  days_left: '7',
  days_inactive: '21',
  accepted_at: '04 Jun 2026',
  resolved_at: '04 Jun 2026',
  closed_at: '04 Jun 2026',
  subscription_period_end: '04 Jul 2026',
  start_time: '05 Jun 2026, 10:00 PM',
  end_time: '06 Jun 2026, 02:00 AM',
  period_label: '29 May 2026 → 04 Jun 2026',
  cadence: 'weekly',

  // ── Auth / credentials ──────────────────────────────────────────
  temporary_password: 'Temp-7M2qB!9p',

  // ── URLs ────────────────────────────────────────────────────────
  sign_in_url: 'https://app.taskmagix.com/sign-in',
  reset_url: 'https://app.taskmagix.com/reset/k_8s2nQ',
  setup_url: 'https://app.taskmagix.com/setup/k_8s2nQ',
  accept_url: 'https://app.taskmagix.com/invite/k_8s2nQ',
  app_url: 'https://app.taskmagix.com',
  task_url: 'https://app.taskmagix.com/tasks/T-2418',
  submit_url: 'https://app.taskmagix.com/reports/daily',
  members_url: 'https://app.taskmagix.com/members',
  upgrade_url: 'https://app.taskmagix.com/billing',
  ticket_url: 'https://app.taskmagix.com/support/T-1024',
  dashboard_url: 'https://app.taskmagix.com/dashboard',

  // ── Plans / billing ─────────────────────────────────────────────
  plan: 'Gold',
  plan_name: 'Gold',
  plan_code: 'gold',
  plan_price: '$23 / month',
  plan_cadence: 'monthly',
  plan_features: 'Unlimited workspaces · Priority support · SOP Galaxy integration',
  plan_trial_days: '14',
  previous_plan_name: 'Free',
  previous_plan_code: 'free',

  // ── Support tickets ─────────────────────────────────────────────
  ticket_number: 'T-1024',
  subject: 'Weekly report not arriving in inbox',
  priority: 'High',
  category: 'Bug',

  // ── Usage / limits ──────────────────────────────────────────────
  credits_used: '8,400',
  credits_limit: '10,000',
  percent_used: '84',
  used_gb: '7.2',
  limit_gb: '10',

  // ── Platform digest stats ───────────────────────────────────────
  active_companies: '142',
  new_companies: '8',
  active_users: '1,247',
  tasks_created: '3,892',
  ai_tokens: '2.1M',
  emails_sent: '24,103',

  // ── Maintenance ─────────────────────────────────────────────────
  panel_name: 'Company panel',
  message: 'Routine database maintenance — read-only mode for ~4 hours.',

  // ── Missed-report admin digest ──────────────────────────────────
  late_count: '3',
  // Pre-rendered HTML so the bullet list shows up properly in the
  // preview AND in a real test send. The runner sends the same shape
  // in production.
  late_members_list:
    '<ul style="margin:0 0 16px;padding-left:20px;line-height:1.8;">' +
    '<li>Alex Morgan — alex.morgan@acme.com</li>' +
    '<li>Jordan Park — jordan.park@acme.com</li>' +
    '<li>Sam Chen — sam.chen@acme.com</li>' +
    '</ul>',
}

/** Look up a sample value by variable name. Falls back to `Sample
 *  <var>` for any variable not yet mapped above — keeps the QA flow
 *  resilient when the events catalogue gains new variables. */
export function sampleForVariable(name) {
  return Object.prototype.hasOwnProperty.call(EMAIL_SAMPLE_VALUES, name)
    ? EMAIL_SAMPLE_VALUES[name]
    : `Sample ${name}`
}
